import { useMemo } from "react";
import { useDatatableContext } from "../contexts/DatatableContextProvider";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { setTotalPages as setStateTotalPages, setTotalItems as setStateTotalItems } from "../contexts/datatableSlice";

interface UseTablePaginationReturn {
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setTotalPages: (totalPages: number) => void;
  setTotalItems: (totalItems: number) => void;
  resetPage: () => void;
}

/**
 * Hook for accessing table pagination state and controls from datatable filters context
 * Note: Pagination filters must be registered by DataTable component
 */
export function useTablePagination(): UseTablePaginationReturn {
  const { getFilterValue, state, dispatch } = useDatatableContext();
  const { updateSearchParamsByKey, deleteSearchParamsByKeyValue } = useFiltersBySearchParams();

  // Get current pagination values from context (handle both numbers and strings from search params)
  const page = useMemo(() => {
    const pageValue = getFilterValue("page");
    return pageValue ? parseInt(pageValue.toString(), 10) : 0;
  }, [getFilterValue]);

  const pageSize = useMemo(() => {
    const pageSizeValue = getFilterValue("pageSize");
    return pageSizeValue ? parseInt(pageSizeValue.toString(), 10) : 20;
  }, [getFilterValue]);

  // Pagination control functions
  const setPage = (newPage: number) => {
    updateSearchParamsByKey({
      page: newPage.toString(),
    });
  };

  const setPageSize = (newPageSize: number) => {
    updateSearchParamsByKey({
      pageSize: newPageSize.toString(),
      page: "0", // Reset to first page when page size changes
    });
  };

  const setTotalPages = (totalPages: number) => {
    dispatch(setStateTotalPages(totalPages));
  };

  const setTotalItems = (totalItems: number) => {
    dispatch(setStateTotalItems(totalItems));
  };

  const resetPage = () => {
    deleteSearchParamsByKeyValue({
      page: undefined,
    });
  };

  return {
    page,
    pageSize,
    totalPages: state.totalPages,
    totalItems: state.totalItems,
    setPage,
    setPageSize,
    setTotalPages,
    setTotalItems,
    resetPage,
  };
}
